/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-observers, ember/no-runloop, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { alias, filterBy } from '@ember/object/computed';
import { cancel, debounce } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Resource from 'linced-app/enums/enrolment-resource';
import State from 'linced-app/enums/enrolment-request-state';
import { hasRole } from 'linced-app/utils/roles';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import { observes } from '@ember-decorators/object';
import config from 'linced-app/config/environment';
import { saveAs } from 'file-saver';
import {
  downloadFromBytes,
  getNzMoeRollReturnFileName,
} from 'linced-app/utils/files';

export default class RollReturn extends Controller {
  // Services
  @service
  fetch;

  @service
  roles;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // pollTimer provides an object to periodically poll the enrolment status
  // endpoint while waiting upon the enrolment service receiving snail-mail
  // envelope back from DEX/ENROL.
  @tracked
  pollTimer = null;

  // State provides the enums for the status endpoint's progress on processing
  // a DEX/ENROL response.
  State = State;

  // Status contains the Status response as returned from the enrolment service.
  status = null;

  @hasRole(Staff.APPROVE_ROLL_RETURNS)
  canApprove;

  @alias('model.rollReturn')
  rollReturn;

  @alias('session.school.jurisdictionalId')
  jurisdictionId;

  @filterBy('model.validationResults', 'isValid', false)
  invalid;

  @filterBy('model.validationResults', 'hasWarnings', true)
  warnings;

  @observes('model.uploadStatuses.[]', 'status', 'status.status')
  pollStatus() {
    if (!isEmpty(this.status)) {
      if (this.status.status <= State.PROCESSING) {
        // We have a Status (request), and it has not yet been processed.
        this.pollTimer = debounce(this, 'updateStatus', 2000, false);
      } else if (!isEmpty(this.pollTimer)) {
        // We've got a result; cancel any pending status poll.
        cancel(this.pollTimer);
        this.pollTimer = null;
      }
    } else if (!isEmpty(this.model.uploadStatuses)) {
      // Check if we received an in-flight request.
      let inFlight = this.model.uploadStatuses.filter((status) => {
        return status.status <= State.PROCESSING;
      });
      if (!isEmpty(inFlight)) {
        this.set('status', inFlight[0]);
      }
    }
  }

  updateStatus() {
    if (isEmpty(this.status)) {
      return;
    }
    this.status.reload().then((status) => {
      switch (status.status) {
        case State.READY:
          // We're done.  Reload the Roll Return entity to get the updated upload fields.
          this.rollReturn.reload();
          // Load the resources, which show the DEX errors.
          status.loadResources();
          break;

        case State.PROCESSING:
          // Still processing - queue another update.
          this.pollTimer = debounce(this, 'updateStatus', 2000, false);
          break;
      }
    });
  }

  @action
  doNothing() {}

  @action
  upload() {
    let request = {
      requesterUsername: this.session.user.username,
      providerId: this.session.school.jurisdictionalId,
      returnId: this.rollReturn.id,
    };

    let status = this.store.createRecord('enrolments/status', {
      schoolId: this.session.school,
      requesterId: this.session.person,
      jurisdiction: Jurisdiction.NZ_MOE,
      resource: Resource.ROLL_RETURN,
      nzMoe: [request],
    });
    this.set('status', status);
    return status.save().then(() => {
      this.model.uploadStatuses.update();
    });
  }

  @action
  downloadMOEFile(rollReturn) {
    let fileName = getNzMoeRollReturnFileName(
      this.jurisdictionId,
      rollReturn,
      '',
      'moe',
    );
    saveAs(
      new Blob([rollReturn.moeFileData], {
        type: 'data:text/csv;charset=utf-8',
      }),
      fileName,
    );
  }

  @action
  viewTables(rollReturn) {
    this.fetch
      .request(
        `${config.APP.apiHost}/providers/nz/moe/v4/rollreturns/rollreturns/${rollReturn.id}/:tables`,
      )
      .then((resp) => {
        let printer = window.open(
          'about:blank',
          '_blank',
          'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=800,width=1280',
        );
        printer.document.body.innerHTML = resp.tableData;
      });
  }

  @action
  downloadStudentLists(rollReturn) {
    this.fetch
      .request(
        `${config.APP.apiHost}/providers/nz/moe/v4/rollreturns/rollreturns/${rollReturn.id}/:lists`,
      )
      .then((resp) => {
        if (!isEmpty(resp.byteData)) {
          let fileName = getNzMoeRollReturnFileName(
            this.jurisdictionId,
            rollReturn,
            ' - Student Lists',
            'zip',
          );
          downloadFromBytes(resp.byteData, 'application/zip', fileName);
        }
      })
      .catch(() => {
        this.toaster.error(`Error downloading student lists!`);
      });
  }
}
